import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from './cn.js';

/**
 * Modern admin settings primitives (2026-10-20).
 *
 * Every section is a card with a title, a one-line explanation and its
 * actions in the header — so the operator always knows what a button does
 * before clicking it, and destructive actions never sit next to a neutral one.
 *
 * Navigation lives in the application sidebar (the Settings module expands
 * into its sections there) — no rail inside the pages.
 */

export interface SettingsCardProps {
  readonly title: string;
  readonly description?: ReactNode;
  readonly icon?: LucideIcon;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
  readonly id?: string;
}

/** A settings section: header (title, description, actions) + body. */
export function SettingsCard({
  title,
  description,
  icon: Icon,
  actions,
  children,
  className,
  id,
}: SettingsCardProps): React.ReactElement {
  return (
    <section
      id={id}
      aria-labelledby={id !== undefined ? `${id}-title` : undefined}
      className={cn('rounded-2xl border border-border bg-card shadow-card', className)}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3.5 sm:px-5">
        <div className="flex min-w-0 items-start gap-3">
          {Icon !== undefined && (
            <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-herb-50 text-herb-700">
              <Icon className="h-4 w-4" aria-hidden="true" />
            </span>
          )}
          <div className="min-w-0">
            <h2
              id={id !== undefined ? `${id}-title` : undefined}
              className="text-sm font-bold leading-snug text-ink"
            >
              {title}
            </h2>
            {description !== undefined && (
              <p className="mt-1 max-w-2xl text-xs leading-relaxed text-ink-soft">{description}</p>
            )}
          </div>
        </div>
        {actions !== undefined && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      <div className="px-4 py-4 sm:px-5">{children}</div>
    </section>
  );
}

export type StatusTone = 'ok' | 'warn' | 'ko' | 'idle' | 'info';

const STATUS_TONE: Record<StatusTone, string> = {
  ok: 'bg-emerald-50 text-emerald-950 ring-emerald-500/30',
  warn: 'bg-amber-50 text-amber-950 ring-amber-500/35',
  ko: 'bg-rose-50 text-rose-950 ring-rose-500/35',
  idle: 'bg-slate-100 text-slate-800 ring-slate-300 font-bold',
  info: 'bg-sky-50 text-sky-950 ring-sky-500/35',
};

const STATUS_DOT: Record<StatusTone, string> = {
  ok: 'bg-emerald-600',
  warn: 'bg-amber-600',
  ko: 'bg-rose-600',
  idle: 'bg-slate-500',
  info: 'bg-sky-600',
};

/** High-contrast status chip with a leading dot (never colour alone). */
export function StatusPill({
  tone,
  children,
  title,
}: {
  readonly tone: StatusTone;
  readonly children: ReactNode;
  readonly title?: string;
}): React.ReactElement {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold ring-1 ring-inset',
        STATUS_TONE[tone],
      )}
    >
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', STATUS_DOT[tone])} aria-hidden="true" />
      {children}
    </span>
  );
}

/** Toolbar row above a list: search field + filters + right-aligned actions. */
export function SettingsToolbar({ children }: { readonly children: ReactNode }): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">{children}</div>
  );
}

/** Search input with a leading icon (used by every settings list). */
export function SettingsSearch({
  value,
  onChange,
  placeholder,
  label,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder: string;
  readonly label: string;
}): React.ReactElement {
  return (
    <div className="relative min-w-0 flex-1 sm:max-w-xs">
      <label className="sr-only" htmlFor={`settings-search-${label}`}>
        {label}
      </label>
      <svg
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-ghost"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        id={`settings-search-${label}`}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 w-full rounded-lg border border-border bg-card pl-9 pr-3 text-sm text-ink shadow-xs placeholder:text-ink-ghost"
      />
    </div>
  );
}

/** Compact select used in toolbars. */
export function SettingsSelect({
  value,
  onChange,
  label,
  options,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly label: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
}): React.ReactElement {
  return (
    <label className="flex items-center gap-2">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-lg border border-border bg-card px-2.5 text-sm font-medium text-ink shadow-xs"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Destructive area — visually separated (red frame) so an operator never
 * deletes something by accident while acting in the safe zone above.
 */
export function DangerZone({
  title,
  description,
  children,
}: {
  readonly title: string;
  readonly description?: ReactNode;
  readonly children: ReactNode;
}): React.ReactElement {
  return (
    <div className="rounded-xl border border-rust-600/30 bg-rust-100/40 p-3.5">
      <p className="text-sm font-bold text-rust-700">{title}</p>
      {description !== undefined && (
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-ink-soft">{description}</p>
      )}
      <div className="mt-2.5">{children}</div>
    </div>
  );
}

/** Empty row inside a list (lighter than the page-level EmptyState). */
export function SettingsEmpty({ children }: { readonly children: ReactNode }): React.ReactElement {
  return (
    <div className="rounded-xl border border-dashed border-border-strong bg-cream/40 px-4 py-6 text-center text-sm text-ink-soft">
      {children}
    </div>
  );
}

/** Inline form panel (add/edit a row) — one consistent look across settings. */
export function SettingsForm({
  onSubmit,
  children,
  className,
}: {
  readonly onSubmit: () => void;
  readonly children: ReactNode;
  readonly className?: string;
}): React.ReactElement {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className={cn(
        'flex flex-wrap items-end gap-2.5 rounded-xl border border-herb-600/30 bg-herb-50/40 p-3',
        className,
      )}
    >
      {children}
    </form>
  );
}

/** Labelled field used inside settings forms. */
export function SettingsField({
  label,
  hint,
  children,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly children: ReactNode;
}): React.ReactElement {
  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span className="text-[11px] font-bold uppercase tracking-wider text-ink-soft">{label}</span>
      {children}
      {hint !== undefined && <span className="text-[11px] text-ink-faint">{hint}</span>}
    </label>
  );
}
